import React, {
  Ref, useEffect, useRef, useState,
} from 'react';
import {
  View, Text, StyleSheet, FlatList, Pressable, ScrollView, ActivityIndicator, Animated, Dimensions,
  Image, LayoutChangeEvent,
} from 'react-native';
import Markdown, { MarkdownIt } from 'react-native-markdown-display';
import blockEmbedPlugin from 'markdown-it-block-embed';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Colors from '../constants/Colors';
import getKnowledgeCenterArticles from '../requests/KnowledgeCenter/GetKnowledgeCenterArticles';
import Style from '../constants/Style';
import isMobile from '../components/IsMobile';
import SwipeLeftIcon from '../components/24pxIcons/SwipeLeftIcon';
import SwipeRightIcon from '../components/24pxIcons/SwipeRightIcon';
import Video from '../components/Video';
import getKnowledgeCenterArticleContent from '../requests/KnowledgeCenter/GetKnowledgeCenterArticleContent';

type KnowledgeCenterArticle = {
  articleId: string,
  markdownUrl: string,
  metadata: { title: string, index: number },
}

const markdownItInstance = MarkdownIt({ typographer: true }).use(blockEmbedPlugin, {
  containerClassName: 'video-embed',
});

const imagePlaceholder = require('../assets/images/image_placeholder.png');

const { width } = Dimensions.get('window');

const KnowledgeCenterScreen = () => {
  const [knowledge, setKnowledge] = useState({});
  const [selectedArticleKey, setSelectedArticleKey] = useState<string>(null);
  const [translateX] = useState<Animated.Value>(new Animated.Value(0));
  const [loading, setLoading] = useState<boolean>(false);
  const { top } = useSafeAreaInsets();
  const scrollView: Ref<ScrollView> = useRef<ScrollView>(null);
  const [mobileContentShown, setMobileContentShown] = useState<boolean>(false);
  const [contentWidth, setContentWidth] = useState<number>(isMobile ? 300 : 800);

  useEffect(() => {
    setLoading(true);
    getKnowledgeCenterArticles().then((res) => {
      if (res && res?.length > 0) {
        const updatedKnowledge = {};
        res?.forEach((item: KnowledgeCenterArticle) => {
          updatedKnowledge[item.articleId] = {
            title: item.metadata.title, index: item.metadata.index,
          };
          setKnowledge(updatedKnowledge);
          getKnowledgeCenterArticleContent(item.markdownUrl).then((content: string) => {
            updatedKnowledge[item.articleId].content = content;
            setKnowledge(updatedKnowledge);
          });
        });
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    Animated.timing(translateX, {
      toValue: mobileContentShown ? -width : 0,
      duration: 500,
      useNativeDriver: true,
    }).start();
    return () => { };
  }, [mobileContentShown]);

  useEffect(() => {
    scrollView?.current?.scrollTo({ y: 0, animated: false });
  }, [selectedArticleKey]);

  const showSectionContent: Function = (key: string) => {
    setSelectedArticleKey(key);
    if (isMobile) setMobileContentShown(true);
  };

  const ImageItem = ({ node }) => {
    const uri: string = node.attributes.src;
    const [aspectRatio, setAspectRatio] = useState<number>(1.6);
    const [imageWidth, setImageWidth] = useState<number>(200);
    Image.getSize(uri, (w, h) => {
      setAspectRatio(w / h);
      setImageWidth(w);
    });
    return <Image
      key={node.key}
      source={{ uri }}
      loadingIndicatorSource={imagePlaceholder}
      resizeMode='contain'
      style={{ width: Math.min(contentWidth, imageWidth) - 50, aspectRatio }}
    />;
  };

  return <View style={[styles.container, isMobile && { paddingTop: top }]}>
    <View style={styles.header}>
      <Text style={styles.title}>{'Knowledge center'}</Text>
    </View>
    <Animated.View style={[styles.subContainer, isMobile && { transform: [{ translateX }] }]}>
      <View style={styles.menu}>
        {isMobile && <View style={styles.contentHeader}>
          <Text style={styles.sectionTitle}>{'Categories'}</Text>
          {selectedArticleKey && <View style={styles.swipeToContent}>
            <Pressable
              onPress={() => setMobileContentShown(true)}
            >
              <SwipeRightIcon />
            </Pressable>
            <Text style={styles.iconTitle}>{'CONTENT'}</Text>
          </View>}
        </View>}
        <FlatList
          data={Object.keys(knowledge).sort((prev: string, next: string) => knowledge[prev].index
            - knowledge[next].index)}
          renderItem={({ item: key }) => {
            if (!knowledge[key]) return null;
            const selected: boolean = selectedArticleKey === key;
            return <Pressable
              style={(state: { hovered: boolean, pressed: boolean }) => [
                styles.menuItem,
                state.hovered && Style.hovered,
                state.pressed && Style.pressed,
                selected && styles.menuItemSelected,
              ]}
              onPress={() => showSectionContent(key)}
            >
              <Text style={[
                styles.menuItemText, isMobile && selected && styles.menuItemTextSelected,
              ]}>
                {knowledge[key].title}
              </Text>
            </Pressable>;
          }}
          keyExtractor={(key) => `knowledge_center_menu_item_${key}`}
        />
      </View>
      <View style={styles.contentWeb}>
        {isMobile && <View style={styles.contentHeader}>
          <Pressable
            onPress={() => setMobileContentShown(false)}
          >
            <SwipeLeftIcon />
            <Text style={styles.iconTitle}>{'CATEGORIES'}</Text>
          </Pressable>
          <Text style={styles.sectionTitle}>{'Content'}</Text>
        </View>}
        <ScrollView
          style={styles.content}
          contentContainerStyle={styles.listContent}
          ref={scrollView}
          testID='kc-text-section'
          onLayout={(event: LayoutChangeEvent) => setContentWidth(event.nativeEvent.layout.width)}
        >
          {!loading && selectedArticleKey && knowledge && knowledge[selectedArticleKey]?.content
            && <Markdown
              children={knowledge[selectedArticleKey].content}
              // children={copy}
              markdownit={markdownItInstance}
              rules={{
                image: (node) => <ImageItem {...{ node }} />,
                video: (node) => {
                  const { serviceName, videoID } = (node as any).sourceInfo;
                  return <Video
                    key={node.key}
                    // @ts-ignore
                    videoId={videoID}
                    {...{ serviceName }}
                    width={contentWidth - 50}
                    height={(contentWidth - 50) / 1.6}
                  />;
                },
              }}
              style={{ body: styles.text }}
            />}
        </ScrollView>
      </View>
    </Animated.View>
    <ActivityIndicator
      size='large'
      style={Style.activityIndicator}
      animating={loading}
    />
  </View>;
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: isMobile ? undefined : 40,
    paddingTop: isMobile ? 24 : 40,
    backgroundColor: Colors.light.hereworksBackground,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24,
    marginHorizontal: isMobile ? 20 : undefined,
  },
  title: {
    fontSize: isMobile ? 22 : 32,
    lineHeight: isMobile ? 33 : 48,
    fontFamily: isMobile ? 'Poppins_600SemiBold' : 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  subContainer: {
    flex: 1,
    borderRadius: 12,
    backgroundColor: '#FFF',
    flexDirection: 'row',
    width: isMobile ? width * 2 : undefined,
  },
  menu: {
    paddingVertical: isMobile ? undefined : 24,
    width: isMobile ? width : 280,
  },
  menuItem: {
    padding: 12,
    borderRadius: 8,
    marginHorizontal: isMobile ? 20 : 12,
    marginVertical: 2,
  },
  menuItemSelected: {
    backgroundColor: Colors.light.hereworksWhite300,
  },
  menuItemText: {
    fontSize: 14,
    lineHeight: 18,
    color: Colors.light.hereworksBlack500,
    fontFamily: 'Poppins_600SemiBold',
  },
  menuItemTextSelected: {
    color: Colors.light.hereworksBlue400,
  },
  contentHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    margin: 20,
    marginTop: 16,
  },
  sectionTitle: {
    fontSize: 18,
    lineHeight: 22.5,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
    marginTop: 4,
  },
  iconTitle: {
    fontSize: 10,
    lineHeight: 12.5,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack400,
    marginTop: 2,
  },
  content: {
    flex: 1,
    padding: 24,
    paddingTop: isMobile ? 0 : undefined,
    borderStartWidth: isMobile ? undefined : 1,
    borderStartColor: isMobile ? undefined : Colors.light.hereworksBlue100,
    maxWidth: 1200,
  },
  swipeToContent: {
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
  },
  contentWeb: {
    flex: 1,
  },
  listContent: {
    flexGrow: 1,
    paddingBottom: isMobile ? 12 : undefined,
  },
  text: {
    fontSize: 16,
    lineHeight: isMobile ? undefined : 24,
    fontFamily: 'Poppins_400Regular',
  },
});

export default KnowledgeCenterScreen;
